import type { StoryMod } from '@goodtime/shared';

/** How a reel modifier is shown and explained. `what` is the one-tap
 *  explainer, in plain words. */
export interface ModInfo {
  /** Big type for the title card and the chip. */
  title: string;
  what: (spins: number, collectorNoun: string) => string;
}

const spinsWord = (n: number) => (n === 1 ? 'your next spin' : `the next ${n} spins`);

export const MOD_INFO: Record<StoryMod, ModInfo> = {
  streak: {
    title: 'LUCKY STREAK',
    what: (n) => `Every win pays double for ${spinsWord(n)}.`,
  },
  bloom: {
    title: 'DOUBLE BLOOM',
    what: (n, noun) => `Your ${noun} count double on the trail for ${spinsWord(n)}.`,
  },
  wild: {
    title: 'WILD REEL',
    what: () => 'The middle reel lands all wild on your next spin.',
  },
  golden: {
    title: 'GOLDEN HOUR',
    what: (n) => `The gold pot grows three times as fast for ${spinsWord(n)}.`,
  },
};

/**
 * A modifier's name on a slot. The collector modifier is named from the
 * slot's own collector symbol: flowers bloom (Forest's "Double Bloom"), and
 * anything else is doubled by name ("Double Banners" on Royal Reels). The
 * others don't depend on a symbol and keep their names everywhere.
 */
export function modTitle(mod: StoryMod, collectorNoun: string): string {
  if (mod !== 'bloom') return MOD_INFO[mod].title;
  const noun = collectorNoun.trim().toUpperCase();
  if (!noun || /FLOWER/.test(noun)) return MOD_INFO.bloom.title;
  return `DOUBLE ${noun}`;
}

/** The modifiers running, in a steady order for the chips. */
export function activeMods(mods: Partial<Record<StoryMod, number>> | undefined): { mod: StoryMod; spins: number }[] {
  const order: StoryMod[] = ['streak', 'bloom', 'wild', 'golden'];
  return order.filter((m) => (mods?.[m] ?? 0) > 0).map((m) => ({ mod: m, spins: mods![m]! }));
}
